import { useState, type FormEvent } from "react";
import FormField, {
  inputClass,
  textareaClass,
  selectClass,
  submitButtonClass,
} from "@/components/base/FormField";
import { submitReaddyForm, validateForm, HONEYPOT_FIELD } from "@/lib/submitForm";

const SUBMIT_ADDR = "https://readdy.ai/api/form/damjr8d1d38lmdrdaqkg";
const SUCCESS_MSG = "已收到你的訊息，謝謝你提供目前的需求。";
const FAIL_MSG = "目前訊息暫時無法送出，請稍後再試，或透過 LINE 與我們聯絡。";

export default function GeneralInquiryForm() {
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState("");

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);
    const hp = (fd.get(HONEYPOT_FIELD) as string)?.trim() || "";
    if (hp) {
      setStatus("success");
      return;
    }
    const errs = validateForm(fd, ["name", "contact", "inquiry_type", "message"]);
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }
    setErrors({});
    setStatus("submitting");
    const res = await submitReaddyForm(form, SUBMIT_ADDR);
    if (res.ok) {
      setStatus("success");
      form.reset();
    } else {
      setStatus("error");
      setErrorMsg(res.message || FAIL_MSG);
    }
  }

  return (
    <form onSubmit={handleSubmit} data-readdy-form id="general-inquiry-form" noValidate>
      <div className="grid grid-cols-1 gap-5">
        <FormField label="姓名" name="name" required error={errors.name}>
          <input id="name" name="name" className={inputClass} placeholder="您的姓名" />
        </FormField>
        <FormField label="Email／LINE／電話" name="contact" required error={errors.contact}>
          <input
            id="contact"
            name="contact"
            className={inputClass}
            placeholder="方便聯繫您的方式"
          />
        </FormField>
        <FormField label="詢問類型" name="inquiry_type" required error={errors.inquiry_type}>
          <select id="inquiry_type" name="inquiry_type" className={selectClass} defaultValue="">
            <option value="" disabled>
              請選擇
            </option>
            <option value="個人成長／課程">個人成長／課程</option>
            <option value="企業與團隊培訓">企業與團隊培訓</option>
            <option value="講師／演講邀約">講師／演講邀約</option>
            <option value="合作洽談">合作洽談</option>
          </select>
        </FormField>
        <FormField label="需求描述" name="message" required error={errors.message}>
          <textarea
            id="message"
            name="message"
            className={textareaClass}
            maxLength={500}
            placeholder="您目前最想了解或解決的問題"
          />
        </FormField>
        <FormField label="補充說明" name="note">
          <textarea
            id="note"
            name="note"
            className={textareaClass}
            maxLength={500}
            placeholder="其他想補充的內容（選填）"
          />
        </FormField>
      </div>

      <input
        type="text"
        name={HONEYPOT_FIELD}
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
        className="form-aux-field"
      />

      {status === "success" && (
        <p className="mt-5 rounded-md bg-success/10 border border-success/30 px-4 py-3 text-sm text-success">
          {SUCCESS_MSG}
        </p>
      )}
      {status === "error" && (
        <p className="mt-5 rounded-md bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-600">
          {errorMsg}
        </p>
      )}

      <div className="mt-6">
        <button type="submit" disabled={status === "submitting"} className={submitButtonClass}>
          {status === "submitting" ? "送出中…" : "送出詢問"}
        </button>
      </div>

      <p className="mt-4 text-xs leading-[1.7] text-ink-soft/70">
        送出表單即表示您同意本網站依
        <a href="#" className="text-primary hover:text-gold underline">
          隱私權政策
        </a>
        使用您所提供的聯絡資料，以回覆本次詢問與提供相關服務資訊。
      </p>
    </form>
  );
}